import { useEffect, useState } from 'react';
import { router, usePage } from '@inertiajs/react';
import AppShell from '../../../components/layout/AppShell';
import SentenceDisplay from '../../../components/sentence_studio/SentenceDisplay';
import ParseLayers from '../../../components/sentence_studio/ParseLayers';
import ContextWindow from '../../../components/sentence_studio/ContextWindow';
import DynamicIsland from '../../../components/ui/DynamicIsland';
import { useBreadcrumbs } from '../../../hooks/useBreadcrumbs';
import { truncateWords } from '../../../lib/truncateWords';

export default function StudentSentenceStudioShow({ analysis, visit, from_chapter, ready, parse_error }) {
  const { auth } = usePage().props;
  const { replace } = useBreadcrumbs();
  const [lookupError, setLookupError] = useState(null);

  useEffect(() => {
    if (ready || parse_error) return;
    const id = setInterval(() => {
      router.reload({ only: ['analysis', 'visit', 'ready', 'parse_error'], preserveScroll: true });
    }, 1500);
    return () => clearInterval(id);
  }, [ready, parse_error]);

  useEffect(() => {
    const crumbs = [];
    if (from_chapter) {
      crumbs.push({
        level: 'book',
        label: from_chapter.book_title,
        href: `/student/sentence_studio?book=${from_chapter.book_id}`, // students don't yet have a book-show; this is a soft fallback
      });
      crumbs.push({
        level: 'chapter',
        label: from_chapter.label || from_chapter.title,
      });
    }
    crumbs.push({
      level: 'sentence',
      label: truncateWords(analysis.text, 40),
      href: `/student/sentence_studio/${analysis.id}${from_chapter ? `?from_chapter=${from_chapter.id}` : ''}`,
    });
    replace(crumbs);
  }, [analysis.id, from_chapter?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  async function onWordClick(token) {
    setLookupError(null);
    // Students navigate within their own dictionary surface; for Session 2 we
    // just surface the looked-up word inline since the student dictionary
    // resolve endpoint isn't built yet.
    const q = token.lemma || token.text;
    try {
      const res = await fetch(`/student/dictionary/search?q=${encodeURIComponent(q)}&limit=1`);
      if (res.ok) {
        const data = await res.json();
        const first = (data.results || data.words || [])[0];
        if (first?.id) {
          window.location.href = `/student/dictionary/${first.id}`;
          return;
        }
      }
      setLookupError(`No dictionary entry for "${q}"`);
    } catch {
      setLookupError('Lookup failed — network error');
    }
  }

  return (
    <AppShell student={auth?.user} scrollTitle={truncateWords(analysis.text, 40)}>
      <div className="max-w-3xl mx-auto space-y-6">
        <div>
          <SentenceDisplay analysis={analysis} onWordClick={onWordClick} />
          {lookupError && (
            <p className="mt-2 text-sm text-rose-500">{lookupError}</p>
          )}
        </div>

        <ContextWindow
          analysisText={analysis.text}
          contextBefore={visit?.context_before}
          contextAfter={visit?.context_after}
          sourceChapter={from_chapter}
        />

        {ready ? (
          <ParseLayers analysis={analysis} />
        ) : parse_error ? (
          <div className="rounded-xl border border-rose-200 dark:border-rose-900/40 bg-rose-50 dark:bg-rose-900/20 p-4 text-sm text-rose-700 dark:text-rose-300">
            Couldn&rsquo;t analyze this sentence: {parse_error}
          </div>
        ) : (
          <div className="rounded-xl border border-dashed border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 p-6 text-center text-sm text-gray-500 dark:text-gray-400">
            Analysis in progress…
          </div>
        )}
      </div>

      <DynamicIsland
        isActive={!ready && !parse_error}
        steps={[
          'Reading sentence…',
          'Tagging parts of speech…',
          'Identifying phrases…',
          'Tracing clause structure…',
          'Finalizing analysis…',
        ]}
        stepDuration={1800}
      />
    </AppShell>
  );
}
